import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authdApiRequest } from '../utils/api';
import Layout from '../components/Layout';

const TRIAGE_LEVELS = [
  { value: 0, label: 'Not Set' },
  { value: 1, label: 'Keep Monitoring' },
  { value: 2, label: 'Make Improvements' },
  { value: 3, label: 'Needs Support' }
];

function TriageResults() {
  const { snapshotId, teamId } = useParams();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [triageData, setTriageData] = useState({});
  const [teamName, setTeamName] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'ascending' });
  const [snapshotTitle, setSnapshotTitle] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch results for the specific team
        const resultsResponse = await authdApiRequest(
          `${process.env.REACT_APP_API_HOST}/surveys/${snapshotId}/results`
        );
        const resultsData = await resultsResponse.json();
        
        // Find the team name from the first topic's teams array
        const team = resultsData.topics[0]?.teams?.find(t => t.teamId.toString() === teamId);
        setTeamName(team?.teamName || '');
        
        // Filter results for the selected team
        const teamResults = resultsData.topics?.map(topic => {
          const teamData = topic.teams?.find(t => t.teamId.toString() === teamId);
          return {
            topicId: topic.topicId,
            topicName: topic.topicName,
            score: teamData?.score || 0,
            focusCount: teamData?.focusCount || 0,
            rank: teamData?.rank,
            focusRank: teamData?.focusRank
          };
        }) || [];

        const snapTitle = `${formatDate(resultsData.snapshot.endDate)} Snapshot`;

        setResults(teamResults);
        setSnapshotTitle(snapTitle);

        // Fetch existing triage data
        const triageResponse = await authdApiRequest(
          `${process.env.REACT_APP_API_HOST}/surveys/${snapshotId}/teams/${teamId}/topic-triage`
        );
        const triageData = await triageResponse.json();
        
        // Convert the array of topic statuses into an object keyed by topicId
        const triageObject = triageData.topics?.reduce((acc, item) => ({
          ...acc,
          [item.topicId]: item.status
        }), {}) || {};
        
        setTriageData(triageObject);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load triage data');
        // Set default empty values in case of error
        setResults([]);
        setTriageData({});
        setTeamName('');
      }
    };

    fetchData();
  }, [snapshotId, teamId]);

  const handleTriageChange = (topicId, value) => {
    setTriageData(prev => ({
      ...prev,
      [topicId]: parseInt(value)
    }));
  };

  const handleSubmit = async () => {
    try {
      // Transform triageData object into array of {topicId, status} objects
      const formattedData = {
        topicStatuses: Object.entries(triageData).map(([topicId, status]) => ({
          topicId: parseInt(topicId),
          status
        }))
      };

      console.log('Sending triage data:', formattedData);

      await authdApiRequest(
        `${process.env.REACT_APP_API_HOST}/surveys/${snapshotId}/teams/${teamId}/topic-triage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formattedData)
        }
      );
      toast.success('Triage data saved successfully');
      navigate(`/snapshots/${snapshotId}/results`);
    } catch (error) {
      console.error('Error saving triage data:', error);
      toast.error('Failed to save triage data');
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Add sorting function
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Add sorted results memo
  const sortedResults = React.useMemo(() => {
    let sortableResults = [...results];
    if (sortConfig !== null) {
      sortableResults.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle percentage values
        if (sortConfig.key === 'score') {
          aValue = a.score || 0;
          bValue = b.score || 0;
        }

        // Handle null/undefined values
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableResults;
  }, [results, sortConfig]);

  return (
    <Layout>
      <div className="text-center mb-4">
        <button
          onClick={() => navigate(`/snapshots/${snapshotId}/results`)}
          className="text-sky-500 hover:underline"
        >
          Back to Snapshot
        </button>
      </div>

      <h2 className="text-lg font-bold mb-2 text-center">
        {snapshotTitle}
      </h2>
      <h1 className="text-2xl font-bold mb-8 text-center">
        Triage Results for {teamName}
      </h1>
      
      <table className="w-full bg-neutral-900 rounded-lg border border-neutral-700">
        <thead>
          <tr>
            <th 
              onClick={() => requestSort('topicName')}
              className="text-left py-2 px-4 border-b-2 border-neutral-700 cursor-pointer hover:bg-neutral-800"
            >
              Topic {sortConfig.key === 'topicName' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => requestSort('score')}
              className="text-center py-2 px-4 border-b-2 border-neutral-700 cursor-pointer hover:bg-neutral-800"
            >
              Score/Rank {sortConfig.key === 'score' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
            </th>
            <th 
              onClick={() => requestSort('focusCount')}
              className="text-center py-2 px-4 border-b-2 border-neutral-700 cursor-pointer hover:bg-neutral-800"
            >
              Priority {sortConfig.key === 'focusCount' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
            </th>
            <th className="text-center py-2 px-4 border-b-2 border-neutral-700">
              Triage Level
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedResults.map((topic) => (
            <tr key={topic.topicId}>
              <td className="border-b border-neutral-700 py-2 px-4">{topic.topicName}</td>
              <td className="border-b border-neutral-700 py-2 px-4 text-center">
                <div className="flex items-center justify-center space-x-2">
                  <span>{topic.rank ? `#${topic.rank}` : 'N/A'}</span>
                  <span>{topic.score !== null ? `(${(topic.score * 100).toFixed()}%)` : 'N/A'}</span> 
                </div>
              </td>
              <td className="border-b border-neutral-700 py-2 px-4 text-center">
                {topic.focusRank ? `#${topic.focusRank} (${topic.focusCount})` : `N/V`}
              </td>
              <td className="border-b border-neutral-700 py-2 px-4 text-center">
                <select
                  value={triageData[topic.topicId] ?? ''}
                  onChange={(e) => handleTriageChange(topic.topicId, e.target.value)}
                  className={`p-2 border rounded bg-neutral-800 ${
                    triageData[topic.topicId] === undefined ? 'border-dashed' : 'border-solid'
                  }`}
                >
                  {TRIAGE_LEVELS.map((level) => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="mt-8 flex justify-center">
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-sky-500 text-white rounded-md"
        >
          {Object.keys(triageData).length > 0 ? 'Update Triage Results' : 'Save Triage Results'}
        </button>
      </div>
    </Layout>
  );
}

export default TriageResults; 